import React, { useState, useEffect, ChangeEvent, useContext } from 'react';
import { MissionaryDashboard, MissionaryForm } from './MissionaryDashboard';
import Header from './Header';
import Footer from './Footer';
import { GlobalContext } from '../context/GlobalContext';
import { deleteMissionary, getMissionariesByUser } from '../supabase/supabase';
import { Missionary } from '../supabase/types';
import { useNavigate } from 'react-router';
import { Button, Modal, Form } from 'react-bootstrap';
import { RiAddLine, RiDeleteBin6Line, RiEditBoxFill, RiSettings4Fill } from 'react-icons/ri';
import LoadingLogo from './LoadingLogo';

export default function Dashboard() {
    const navigate = useNavigate();
    const [missionaries, setMissionaries] = useState<Missionary[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [showMissionaryForm, setShowMissionaryForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(true);

    const openMissionaryForm = () => setShowMissionaryForm(true);
    const closeMissionaryForm = () => setShowMissionaryForm(false);

    const global = useContext(GlobalContext);

    const initDashboard = async () => {
        console.log('Global.session?', global.session);
        if (global.session) {
            const msns = await getMissionariesByUser(global.session.user);
            if (msns.length > 0) {
                setMissionaries(msns);
                setSelectedIndex(0);
                setLoading(false);
            } else {
                setLoading(false);
                openMissionaryForm();
            }
        } else {
            // redirect to login page
            navigate('/login');
        }
    };

    useEffect(() => {
        initDashboard();
    }, []);

    const handleCloseForm = async (missionaryEmail: string | undefined) => {
        await initDashboard();

        if (missionaryEmail) {
            const index = missionaries.findIndex((m) => m.email === missionaryEmail);
            if (index >= 0) {
                setSelectedIndex(index);
            }
        }
        closeMissionaryForm();
    };

    const handleCloseEditForm = async (missionaryEmail: string | undefined) => {
        await initDashboard();

        if (missionaryEmail) {
            const index = missionaries.findIndex((m) => m.email === missionaryEmail);
            if (index >= 0) {
                setSelectedIndex(index);
            }
        }
        setShowEditForm(false);
    };

    const ActionButton = () => {
        return (
            <div className="modern-action-button">
                <div className="action-button">
                    <Button className="main-button" variant="primary">
                        <RiSettings4Fill />
                    </Button>
                    <div className="expanded-buttons">
                        <Button
                            variant="primary"
                            className="action-item"
                            onClick={() => setShowEditForm(true)}
                            title="Edit Missionary"
                        >
                            <RiEditBoxFill />
                        </Button>
                        <Button
                            variant="danger"
                            className="action-item"
                            onClick={() => setShowDeleteModal(true)}
                            title="Delete Missionary"
                        >
                            <RiDeleteBin6Line />
                        </Button>
                        <Button
                            variant="success"
                            className="action-item"
                            onClick={openMissionaryForm}
                            title="Add New Missionary"
                        >
                            <RiAddLine />
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const DeleteConfirmationModal = ({
        show,
        onHide,
        missionary,
        onDelete
    }: {
        show: boolean;
        onHide: () => void;
        missionary?: Missionary;
        onDelete: () => Promise<void>;
    }) => {
        const [isDeleting, setIsDeleting] = useState(false);
        const [error, setError] = useState('');
        const [typedName, setTypedName] = useState('');

        const handleDelete = async () => {
            setIsDeleting(true);
            setError('');
            try {
                await onDelete();
                onHide();
            } catch (err: any) {
                setError(err.message || 'An error occurred while deleting');
            } finally {
                setIsDeleting(false);
            }
        };

        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="alert alert-danger mb-3">{error}</div>}
                    {missionary && (
                        <p>
                            Are you sure you want to delete {missionary.name}? This will remove all
                            associated data and cannot be undone.
                            <Form.Control
                                placeholder="Type missionary's name to confirm."
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                            />
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={isDeleting || typedName !== missionary?.name}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    // Then in your Dashboard component:
    const handleDelete = async () => {
        if (!missionaries[selectedIndex]) return;

        await deleteMissionary(missionaries[selectedIndex]);

        // const newMissionaries = missionaries.filter((_, index) => index !== selectedIndex);
        // setMissionaries(newMissionaries);

        // if (selectedIndex >= newMissionaries.length) {
        //     setSelectedIndex(Math.max(0, newMissionaries.length - 1));
        // }

        await initDashboard();
    };

    return (
        <div className="dashboard-page d-flex flex-column min-vh-100">
            <Header />
            
            {loading && (
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <LoadingLogo />
                </div>
            )}
            
            {!loading && (
                <div className="dashboard-content">
                    <ActionButton />
                    
                    {missionaries.length > 1 && (
                        <div className="missionary-tabs-container">
                            <div className="missionary-tabs">
                                {missionaries.map((missionary, index) => (
                                    <button
                                        key={index}
                                        className={`missionary-tab ${selectedIndex === index ? 'active' : ''}`}
                                        onClick={() => setSelectedIndex(index)}
                                    >
                                        <div className="tab-content">
                                            <div className="tab-name">{missionary.name}</div>
                                            <div className="tab-email">{missionary.email}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {missionaries.length > 0 && (
                        <MissionaryDashboard missionary={missionaries[selectedIndex]} />
                    )}
                </div>
            )}
            
            <MissionaryForm show={showMissionaryForm} handleClose={handleCloseForm} />
            <MissionaryForm
                show={showEditForm}
                handleClose={handleCloseEditForm}
                missionary={missionaries[selectedIndex]}
                isEdit={true}
            />
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                missionary={missionaries[selectedIndex]}
                onDelete={handleDelete}
            />
            <Footer />
        </div>
    );
}
